/**
 * arvexisAudio.js
 * ================
 * 
 * Centralized audio management for Arvexis voice experiences.
 * 
 * This module handles:
 * - Audio cache that survives React component remounts
 * - Parallel TTS fetching with sentence-1 prioritization
 * - Sequential playback with iOS-compatible unlocked elements
 * - Consistent volume and voice selection
 * - Object URL management with proper cleanup
 * 
 * CRITICAL iOS RULES:
 * 1. Audio elements MUST be created inside a user gesture handler
 * 2. audio.load() MUST be called synchronously in that handler
 * 3. Later, we can set src and call play() on those pre-unlocked elements
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Master volume for all Arvexis audio playback.
 * Applied to every audio element. Never set anywhere else in the app.
 * 
 * Calibrated for calm presence — Arvexis should feel comfortable to listen to,
 * not startlingly loud. This supports the conversational design principle that
 * presence comes through attention and pacing, not volume.
 */
export const ARVEXIS_VOLUME = 0.7;

/**
 * TTS speed setting for natural speech pacing.
 */
const TTS_SPEED = 0.88;

// =============================================================================
// AUDIO CACHE — Global state that survives React remounts
// =============================================================================

/**
 * Global audio cache object.
 * Persists across React Strict Mode unmount/remount cycles.
 */
export const audioCache = {
  clips: {},           // { [index]: base64AudioString }
  objectUrls: {},      // { [index]: objectUrlString } - for cleanup
  durations: {},       // { [index]: estimatedDurationMs }
  generationStartedAt: null,
  generationCompletedAt: null,
  isGenerating: false,
  isComplete: false,
  clipCount: 0,        // Total expected clips
  readyCount: 0,       // Number of clips ready
};

/**
 * Resets the audio cache to initial state.
 * Revokes any existing object URLs.
 */
export function resetAudioCache() {
  // Revoke all object URLs
  Object.values(audioCache.objectUrls).forEach(url => {
    if (url) {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {
        // Ignore errors on cleanup
      }
    }
  });
  
  audioCache.clips = {};
  audioCache.objectUrls = {};
  audioCache.durations = {};
  audioCache.generationStartedAt = null;
  audioCache.generationCompletedAt = null;
  audioCache.isGenerating = false;
  audioCache.isComplete = false;
  audioCache.clipCount = 0;
  audioCache.readyCount = 0;
  console.log('[arvexisAudio] Cache reset');
}

// =============================================================================
// VOICE SELECTION
// =============================================================================

/**
 * 6D: this file no longer names a voice. Onboarding audio goes to /api/companion/tts, which
 * resolves the member's one canonical voice server-side (resolve_voice). Naming a voice here
 * could only ever disagree with that resolution, so the hardcode is gone rather than corrected.
 */

// =============================================================================
// TEXT SANITIZATION — Clean punctuation artifacts before TTS
// =============================================================================

/**
 * Sanitizes text for TTS - remove ALL punctuation that might be spoken aloud.
 * CRITICAL: Remove ALL types of quotation marks completely.
 */
function sanitizeForTTS(text) {
  return text
    // Remove ALL quotation marks - every possible type
    .replace(/"/g, '')
    .replace(/'/g, '')
    .replace(/"/g, '')
    .replace(/"/g, '')
    .replace(/'/g, '')
    .replace(/'/g, '')
    .replace(/«/g, '')
    .replace(/»/g, '')
    .replace(/„/g, '')
    .replace(/‟/g, '')
    // Remove ellipsis
    .replace(/…/g, ',')
    .replace(/\.\.\./g, ',')
    // Remove em-dash and en-dash
    .replace(/—/g, ',')
    .replace(/–/g, ',')
    // Clean up
    .replace(/,\s*,/g, ',')
    .replace(/\s+/g, ' ')
    .replace(/,\s*\./g, '.')
    .trim();
}

// =============================================================================
// CACHE PRIMING — Parallel fetch with sentence-1 prioritization
// =============================================================================

/**
 * Fetches a single TTS clip and stores it in cache.
 * Returns immediately when complete, allowing progressive availability.
 */
async function fetchSingleClip(line, index, lang, batchStartTime) {
  const fetchStart = Date.now();
  const relativeStart = fetchStart - batchStartTime;
  
  // Sanitize text to prevent punctuation artifacts
  const sanitizedText = sanitizeForTTS(line.text);
  
  try {
    const response = await fetch(`${API_URL}/api/companion/tts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        text: sanitizedText,
        speed: TTS_SPEED,
        language: lang,
      }),
    });
    
    const responseTime = Date.now() - batchStartTime;
    
    if (!response.ok) {
      console.error(`[arvexisAudio] Clip ${index + 1} HTTP ${response.status} at T+${responseTime}ms`);
      return false;
    }
    
    const data = await response.json();
    const completeTime = Date.now() - batchStartTime;
    const base64Audio = data.audio;
    
    if (!base64Audio) {
      console.error(`[arvexisAudio] Clip ${index + 1} empty at T+${completeTime}ms`);
      return false;
    }
    
    // Store base64 in cache
    audioCache.clips[index] = base64Audio;
    
    // Estimate duration from base64 length
    const base64Length = base64Audio.length;
    const estimatedSeconds = base64Length / 1.33 / 16000;
    const estimatedMs = Math.round(estimatedSeconds * 1000);
    audioCache.durations[index] = estimatedMs;
    
    audioCache.readyCount++;
    
    console.log(
      `[arvexisAudio] ⏱️ Clip ${index + 1} READY at T+${completeTime}ms ` +
      `(started T+${relativeStart}ms, took ${completeTime - relativeStart}ms)`
    );
    
    return true;
  } catch (error) {
    const errorTime = Date.now() - batchStartTime;
    console.error(`[arvexisAudio] Clip ${index + 1} ERROR at T+${errorTime}ms:`, error);
    return false;
  }
}

// =============================================================================
// STATIC AUDIO CACHE PRIMING — Load pre-rendered .mp3 files (no TTS API calls)
// =============================================================================

/**
 * Fetches a single static .mp3 file and stores it in cache.
 * Used for First Meeting onboarding — loads pre-approved audio files.
 */
async function fetchStaticAudioClip(line, index, batchStartTime) {
  const fetchStart = Date.now();
  const relativeStart = fetchStart - batchStartTime;
  
  const audioFile = line.audioFile;
  if (!audioFile) {
    console.error(`[arvexisAudio] Static clip ${index + 1} has no audioFile path`);
    return false;
  }
  
  try {
    const response = await fetch(audioFile);
    const responseTime = Date.now() - batchStartTime;
    
    if (!response.ok) {
      console.error(`[arvexisAudio] Static clip ${index + 1} HTTP ${response.status} at T+${responseTime}ms`);
      return false;
    }
    
    // Get the audio as a blob, then convert to base64
    const blob = await response.blob();
    const completeTime = Date.now() - batchStartTime;
    
    // Convert blob to base64 for consistent cache format
    const base64Audio = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Strip data URL prefix to get raw base64
        const dataUrl = reader.result;
        const base64 = dataUrl.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    
    if (!base64Audio) {
      console.error(`[arvexisAudio] Static clip ${index + 1} empty at T+${completeTime}ms`);
      return false;
    }
    
    // Store base64 in cache
    audioCache.clips[index] = base64Audio;
    
    // Estimate duration from blob size (MP3 at ~128kbps = ~16KB per second)
    const estimatedSeconds = blob.size / 16000;
    const estimatedMs = Math.round(estimatedSeconds * 1000);
    audioCache.durations[index] = estimatedMs;
    
    audioCache.readyCount++;
    
    console.log(
      `[arvexisAudio] ⏱️ Static clip ${index + 1} READY at T+${completeTime}ms ` +
      `(started T+${relativeStart}ms, took ${completeTime - relativeStart}ms)`
    );
    
    return true;
  } catch (error) {
    const errorTime = Date.now() - batchStartTime;
    console.error(`[arvexisAudio] Static clip ${index + 1} ERROR at T+${errorTime}ms:`, error);
    return false;
  }
}

/**
 * Loads all static .mp3 audio clips in parallel.
 * Used for First Meeting onboarding with pre-rendered audio files.
 * 
 * Script lines must have an `audioFile` property with the path to the .mp3 file.
 * Example: { text: "Hi there.", audioFile: "/audio/onboarding/english/line-01.mp3" }
 * 
 * NO TTS API CALLS — purely static asset loading.
 */
export async function primeStaticAudioCache(script) {
  // Prevent duplicate generation
  if (audioCache.isGenerating) {
    console.log('[arvexisAudio] Static cache priming already in progress, skipping');
    return false;
  }
  
  if (audioCache.isComplete && audioCache.readyCount === script.length) {
    console.log('[arvexisAudio] Static cache already complete, skipping');
    return true;
  }
  
  audioCache.isGenerating = true;
  audioCache.generationStartedAt = Date.now();
  audioCache.clipCount = script.length;
  audioCache.readyCount = 0;
  
  console.log(`[arvexisAudio] ⏱️ STATIC CACHE START at T=0`);
  console.log(`[arvexisAudio] Loading ${script.length} static .mp3 files NOW (no TTS API calls)`);
  
  // Create array of promises WITHOUT awaiting - all fire simultaneously
  const startTime = Date.now();
  const promises = [];
  
  for (let i = 0; i < script.length; i++) {
    const line = script[i];
    const fireTime = Date.now() - startTime;
    console.log(`[arvexisAudio] ⏱️ Static clip ${i + 1} FIRING at T+${fireTime}ms: ${line.audioFile}`);
    
    // Push the promise immediately - do NOT await
    promises.push(fetchStaticAudioClip(line, i, startTime));
  }
  
  const allFired = Date.now() - startTime;
  console.log(`[arvexisAudio] ⏱️ All ${script.length} static requests FIRED in ${allFired}ms (should be <50ms)`);
  
  // NOW wait for all to complete
  const results = await Promise.all(promises);
  const allSucceeded = results.every(r => r === true);
  
  audioCache.generationCompletedAt = Date.now();
  audioCache.isGenerating = false;
  audioCache.isComplete = allSucceeded;
  
  const totalTime = audioCache.generationCompletedAt - audioCache.generationStartedAt;
  
  console.log(`[arvexisAudio] ⏱️ STATIC CACHE COMPLETE at T+${totalTime}ms`);
  console.log(`[arvexisAudio] Status: ${allSucceeded ? 'SUCCESS' : 'PARTIAL'} (${audioCache.readyCount}/${script.length} clips)`);
  
  if (totalTime > 3000) {
    console.warn(`[arvexisAudio] ⚠️ Static cache took ${totalTime}ms - exceeds 3s target`);
  }
  
  return allSucceeded;
}

/**
 * Fetches all TTS audio clips in parallel.
 * All fetches fire immediately — they don't wait for each other.
 * Logs precise timestamps to verify parallel execution.
 */
export async function primeAudioCache(script, lang) {
  // Prevent duplicate generation
  if (audioCache.isGenerating) {
    console.log('[arvexisAudio] Cache priming already in progress, skipping');
    return false;
  }
  
  if (audioCache.isComplete && audioCache.readyCount === script.length) {
    console.log('[arvexisAudio] Cache already complete, skipping');
    return true;
  }
  
  audioCache.isGenerating = true;
  audioCache.generationStartedAt = Date.now();
  audioCache.clipCount = script.length;
  audioCache.readyCount = 0;
  
  console.log(`[arvexisAudio] ⏱️ PARALLEL CACHE START at T=0`);
  console.log(`[arvexisAudio] Firing ${script.length} TTS requests NOW (voice: server-resolved)`);
  
  // Create array of promises WITHOUT awaiting - all fire simultaneously
  const startTime = Date.now();
  const promises = [];
  
  for (let i = 0; i < script.length; i++) {
    const line = script[i];
    const fireTime = Date.now() - startTime;
    console.log(`[arvexisAudio] ⏱️ Clip ${i + 1} FIRING at T+${fireTime}ms`);
    
    // Push the promise immediately - do NOT await
    promises.push(fetchSingleClip(line, i, lang, startTime));
  }
  
  const allFired = Date.now() - startTime;
  console.log(`[arvexisAudio] ⏱️ All ${script.length} requests FIRED in ${allFired}ms (should be <50ms)`);
  
  // NOW wait for all to complete
  const results = await Promise.all(promises);
  const allSucceeded = results.every(r => r === true);
  
  audioCache.generationCompletedAt = Date.now();
  audioCache.isGenerating = false;
  audioCache.isComplete = allSucceeded;
  
  const totalTime = audioCache.generationCompletedAt - audioCache.generationStartedAt;
  
  console.log(`[arvexisAudio] ⏱️ PARALLEL CACHE COMPLETE at T+${totalTime}ms`);
  console.log(`[arvexisAudio] Status: ${allSucceeded ? 'SUCCESS' : 'PARTIAL'} (${audioCache.readyCount}/${script.length} clips)`);
  
  if (totalTime > 5000) {
    console.warn(`[arvexisAudio] ⚠️ Cache took ${totalTime}ms - exceeds 5s target`);
  }
  
  return allSucceeded;
}

/**
 * Checks if a specific clip is ready.
 */
export function isClipReady(index) {
  return !!audioCache.clips[index];
}

// =============================================================================
// iOS AUDIO UNLOCKING
// =============================================================================

/**
 * Creates and unlocks audio elements for iOS Safari.
 * 
 * iOS requires audio elements to be created and have load() called
 * SYNCHRONOUSLY inside a user gesture handler (touchstart/mousedown).
 * 
 * Call this at the START of your gesture handler, before any async work.
 * 
 * @param {number} count - Number of audio elements to create
 * @returns {HTMLAudioElement[]} Array of unlocked audio elements
 */
export function createUnlockedAudioElements(count) {
  console.log(`[arvexisAudio] Creating ${count} unlocked audio elements for iOS`);
  
  const elements = [];
  
  for (let i = 0; i < count; i++) {
    const audio = new Audio();
    audio.volume = ARVEXIS_VOLUME;
    audio.preload = 'auto';
    audio.load(); // This unlocks the element on iOS
    elements.push(audio);
  }
  
  console.log(`[arvexisAudio] ${count} audio elements unlocked`);
  return elements;
}

// =============================================================================
// PLAYBACK PREPARATION
// =============================================================================

/**
 * Prepares a single clip for playback by setting its src.
 * Uses the pre-unlocked audio element.
 * Creates and tracks object URL for cleanup.
 * 
 * @param {HTMLAudioElement} audio - Pre-unlocked audio element
 * @param {number} index - Clip index
 * @returns {{ ready: boolean, duration: number }}
 */
export function prepareClipForPlayback(audio, index) {
  const base64Audio = audioCache.clips[index];
  const duration = audioCache.durations[index] || 3000;
  
  if (!audio) {
    console.error(`[arvexisAudio] No audio element for clip ${index + 1}`);
    return { ready: false, duration };
  }
  
  if (!base64Audio) {
    console.warn(`[arvexisAudio] Clip ${index + 1} not cached yet`);
    return { ready: false, duration };
  }
  
  // Convert base64 to blob and create object URL
  try {
    const byteCharacters = atob(base64Audio);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'audio/mp3' });
    const objectUrl = URL.createObjectURL(blob);
    
    // Store for cleanup
    audioCache.objectUrls[index] = objectUrl;
    
    // Set the src
    audio.src = objectUrl;
    audio.volume = ARVEXIS_VOLUME;
    
    return { ready: true, duration };
  } catch (e) {
    console.error(`[arvexisAudio] Failed to prepare clip ${index + 1}:`, e);
    return { ready: false, duration };
  }
}

/**
 * Revokes the object URL for a clip after playback is complete.
 */
export function cleanupClip(index) {
  const url = audioCache.objectUrls[index];
  if (url) {
    try {
      URL.revokeObjectURL(url);
      delete audioCache.objectUrls[index];
      console.log(`[arvexisAudio] Cleaned up clip ${index + 1} object URL`);
    } catch (e) {
      // Ignore cleanup errors
    }
  }
}

// =============================================================================
// EMERGENCY STOP — Immediately halt all audio
// =============================================================================

/**
 * Global registry of active audio elements.
 * Populated by playSequence, cleared by stopAllAudio.
 */
const activeAudioElements = new Set();

/**
 * Registers an audio element for tracking.
 * Called internally during playback.
 */
export function registerAudioElement(audio) {
  if (audio) {
    activeAudioElements.add(audio);
  }
}

/**
 * Immediately stops ALL Arvexis audio playback.
 * 
 * - Pauses and resets all tracked audio elements
 * - Clears the audio cache
 * - Revokes all object URLs
 * - Clears the active element registry
 * 
 * Call this when the user exits First Meeting via language change.
 */
export function stopAllAudio() {
  console.log('[arvexisAudio] ⛔ EMERGENCY STOP: Halting all audio');
  
  // 1. Stop all tracked audio elements
  activeAudioElements.forEach(audio => {
    try {
      audio.pause();
      audio.currentTime = 0;
      audio.src = '';
      audio.onended = null;
      audio.onerror = null;
    } catch (e) {
      // Ignore errors on individual elements
    }
  });
  activeAudioElements.clear();
  
  // 2. Also find and stop any other audio elements in the document
  try {
    const allAudioElements = document.querySelectorAll('audio');
    allAudioElements.forEach(audio => {
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch (e) {
        // Ignore
      }
    });
  } catch (e) {
    // Ignore errors if DOM query fails
  }
  
  // 3. Reset the audio cache (revokes object URLs)
  resetAudioCache();
  
  console.log('[arvexisAudio] ⛔ All audio stopped and cache cleared');
}

// =============================================================================
// PLAYBACK SEQUENCING
// =============================================================================

/**
 * Plays a sequence of audio clips in order.
 * 
 * Uses onended as primary advance mechanism.
 * Uses duration-based fallback timeout in case onended does not fire (iOS Safari).
 * Prevents duplicate advancement.
 * Cleans up object URLs after each clip finishes.
 * 
 * @param {HTMLAudioElement[]} audioElements - Pre-unlocked audio elements
 * @param {Array<{text: string, pauseAfter?: number}>} script - Dialogue script
 * @param {Object} callbacks
 * @param {Function} callbacks.onLineStart - Called when line starts: (index, text, duration, audio) => void
 * @param {Function} callbacks.onLineEnd - Called when line ends: (index) => void
 * @param {Function} callbacks.onComplete - Called when sequence finishes
 * @returns {Function} Cancel function
 */
export function playSequence(audioElements, script, { onLineStart, onLineEnd, onComplete }) {
  if (!audioElements || audioElements.length === 0) {
    console.warn('[arvexisAudio] playSequence called with no audio elements');
    onComplete?.();
    return () => {};
  }
  
  console.log(`[arvexisAudio] Starting playback sequence: ${script.length} clips`);
  
  let currentIndex = 0;
  let isCancelled = false;
  let fallbackTimer = null;
  
  const playNext = () => {
    if (isCancelled) {
      console.log('[arvexisAudio] Sequence cancelled');
      return;
    }
    
    if (currentIndex >= script.length) {
      console.log('[arvexisAudio] Sequence complete');
      onComplete?.();
      return;
    }
    
    const index = currentIndex;
    const line = script[index];
    const audio = audioElements[index];
    const text = line.text;
    const pauseAfter = line.pauseAfter ?? 600;
    
    // Prepare this clip (sets src from cache)
    const { ready, duration } = prepareClipForPlayback(audio, index);
    
    if (!ready) {
      console.warn(`[arvexisAudio] Clip ${index + 1} not ready, checking if cached...`);
      // Wait a bit and retry if clip might still be loading
      if (!audioCache.clips[index] && audioCache.isGenerating) {
        setTimeout(playNext, 100);
        return;
      }
      // Skip this clip if truly unavailable
      console.error(`[arvexisAudio] Skipping unavailable clip ${index + 1}`);
      currentIndex++;
      setTimeout(playNext, 100);
      return;
    }
    
    console.log(`[arvexisAudio] Playing clip ${index + 1}/${script.length}: "${text.substring(0, 30)}..." (${duration}ms)`);
    
    // Track whether we've already advanced (prevent double-advance)
    let hasAdvanced = false;
    
    const advanceToNext = (source) => {
      if (hasAdvanced || isCancelled) return;
      hasAdvanced = true;
      
      // Clear fallback timer
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
      
      console.log(`[arvexisAudio] Clip ${index + 1} ended (${source})`);
      
      // Clean up this clip's object URL
      cleanupClip(index);
      
      // Notify line end
      onLineEnd?.(index);
      
      // Increment and continue
      currentIndex++;
      
      // Pause between lines, then play next
      setTimeout(playNext, pauseAfter);
    };
    
    // Primary: advance when audio ends naturally
    audio.onended = () => {
      advanceToNext('onended');
    };
    
    // Error handler
    audio.onerror = (e) => {
      console.error(`[arvexisAudio] Audio error on clip ${index + 1}:`, e);
      advanceToNext('error');
    };
    
    // Fallback: advance after duration + buffer (iOS sometimes misses onended)
    const fallbackDelay = Math.max(duration + 1500, 3000);
    fallbackTimer = setTimeout(() => {
      if (!hasAdvanced && !isCancelled) {
        console.warn(`[arvexisAudio] Fallback timeout for clip ${index + 1} (audio.ended=${audio.ended})`);
        advanceToNext('fallback');
      }
    }, fallbackDelay);
    
    // Play the audio
    registerAudioElement(audio); // Track for emergency stop
    const playPromise = audio.play();
    
    if (playPromise) {
      playPromise
        .then(() => {
          console.log(`[arvexisAudio] Clip ${index + 1} now playing`);
          // Notify line start AFTER play succeeds
          onLineStart?.(index, text, duration, audio);
        })
        .catch((error) => {
          console.error(`[arvexisAudio] Play FAILED for clip ${index + 1}:`, error.name, error.message);
          // Notify anyway so UI can show text
          onLineStart?.(index, text, duration, audio);
          // Advance after a delay
          setTimeout(() => advanceToNext('play-error'), 2000);
        });
    } else {
      // No promise (older browsers)
      onLineStart?.(index, text, duration, audio);
    }
  };
  
  // Start the sequence
  playNext();
  
  // Return cancel function
  return () => {
    isCancelled = true;
    if (fallbackTimer) {
      clearTimeout(fallbackTimer);
    }
    console.log('[arvexisAudio] Sequence cancellation requested');
  };
}
